import type { FileForProcessing, IOrder, IPrimer, ISamplePool } from '../../../../Model/index.js'
import type { Taxas } from '../../../../Model/ITaxa.js'
import { tracingAxios } from '../../../../Repository/Sentry.js'
import { Environment } from '../../../../environment.js'
import { AxiosError } from 'axios'

class MismatchingPrimerError extends Error {
  constructor(message: string) {
    super(message)
    Object.setPrototypeOf(this, MismatchingPrimerError.prototype)
  }
}

export class MismatchingAmpliconError extends Error {
  /** The amplicon the order targets. */
  expectedAmplicon: Taxas
  /** The known primer(s) whose sequences matched, but which are registered for a different amplicon. */
  foundPrimers: IPrimer[]

  constructor(message: string, expectedAmplicon: Taxas, foundPrimers: IPrimer[]) {
    super(message)
    Object.setPrototypeOf(this, MismatchingAmpliconError.prototype)
    this.expectedAmplicon = expectedAmplicon
    this.foundPrimers = foundPrimers
  }
}

class AmbiguuousPrimerError extends Error {
  samplePrimers: IPrimer[] = []

  constructor(message: string, samplePrimers: IPrimer[]) {
    super(message)
    Object.setPrototypeOf(this, AmbiguuousPrimerError.prototype)
    this.samplePrimers = samplePrimers
  }
}

export class PrimerNotFoundError extends Error {
  samplePrimers: string[] = []

  constructor(message: string, samplePrimers: string[]) {
    super(message)
    Object.setPrototypeOf(this, PrimerNotFoundError.prototype)
    this.samplePrimers = samplePrimers
  }
}

export async function validatePrimers(order: IOrder, samplePools: ISamplePool[]): Promise<[ISamplePool[], IPrimer | null]> {
  if (samplePools.length == 0) return [samplePools, null]
  if (!samplePools[0].samples || samplePools[0].samples.length == 0) return [samplePools, null]

  const fwdPrimer = samplePools[0].samples[0].forwardPrimer
  const reversePrimer = samplePools[0].samples[0].reversePrimer
  let canonicalPrimer: IPrimer | null = null
  let canonicalPrimers: IPrimer[] = []
  let taxa = order.targetTaxonomy

  let allMatches: IPrimer[] = []
  try {
    allMatches = (
      await tracingAxios.get<IPrimer[]>(`${Environment.API_URL}/v1/primer`, {
        params: {
          forwardSequence: fwdPrimer,
          reverseSequence: reversePrimer
        },
        withCredentials: true
      })
    ).data
  } catch (error) {
    if (error instanceof AxiosError) {
      if (error.status === 404)
        throw new PrimerNotFoundError('Primer was not found in the Database!', [fwdPrimer || 'N/A', reversePrimer || 'N/A'])
    }
    throw error
  }

  /* filter down to all for correct taxa */
  canonicalPrimers = allMatches.filter((p) => p.amplicon === taxa)

  if (canonicalPrimers.length == 0) {
    /*
     * Two very different situations used to collapse into one PrimerNotFoundError here:
     *  - the sequences matched known primer(s) but they're registered for a DIFFERENT amplicon than
     *    this order targets -> the customer most likely picked the wrong target taxonomy (or the wrong
     *    samples). Surface this distinctly so the UI can explain the conflict instead of asking them to
     *    submit a "new" primer that already exists in our database.
     *  - the sequences matched nothing at all -> the primer is genuinely unknown to us ("submit to us").
     */
    if (allMatches.length > 0) {
      console.info(
        'primer amplicon mismatch, expected',
        taxa,
        'got',
        allMatches.map((p) => p.amplicon)
      )
      throw new MismatchingAmpliconError(
        `The provided primer is registered for a different amplicon than this order's target (${taxa}).`,
        taxa,
        allMatches
      )
    }
    throw new PrimerNotFoundError('Primer was not found in the Database!', [fwdPrimer || 'N/A', reversePrimer || 'N/A'])
  }
  if (canonicalPrimers.length > 1)
    throw new AmbiguuousPrimerError(
      `Primer was found in the Database! Could not uniquely identify primer in question, please contact support or fix your primer setup. Got ${canonicalPrimers.length} Primers, expected 1`,
      canonicalPrimers
    )
  canonicalPrimer = canonicalPrimers[0]

  //check all samples if they have the right forwardSequence
  samplePools.forEach((sp) => {
    function doesItMatchPrimer(primer: IPrimer, item: FileForProcessing): boolean {
      return (
        !!item.forwardPrimer &&
        !!item.reversePrimer &&
        ((item.forwardPrimer.endsWith(primer.forwardSequence) && item.reversePrimer.endsWith(primer.reverseSequence)) ||
          (item.forwardPrimer.endsWith(primer.reverseSequence) && item.reversePrimer.endsWith(primer.forwardSequence)))
      )
    }

    const differingSample = sp.samples?.find((s) => !doesItMatchPrimer(canonicalPrimer, s))
    if (differingSample)
      throw new MismatchingPrimerError(
        `Samples do not share the same Canonical Primer! Got Canonical Primer ${canonicalPrimer.forwardSequence},${canonicalPrimer.reverseSequence}, but sample with file ${differingSample.forwardFile?.fileName} in samplePool ${sp.id} has ${differingSample.forwardPrimer},${differingSample.reversePrimer}`
      )
    const differingNegativeControl = sp.negativeControls?.find((s) => !doesItMatchPrimer(canonicalPrimer, s))
    if (differingNegativeControl)
      throw new MismatchingPrimerError(
        `Samples do not share the same Canonical Primer! Got Canonical Primer ${canonicalPrimer.forwardSequence},${canonicalPrimer.reverseSequence}, but sample with file ${differingNegativeControl.forwardFile?.fileName} in samplePool ${sp.id} has ${differingNegativeControl.forwardPrimer},${differingNegativeControl.reversePrimer}`
      )
  })
  const updatedSamplePools: ISamplePool[] = JSON.parse(JSON.stringify(samplePools))
  updatedSamplePools.forEach((sp) => {
    const samplesCopy = sp.samples
      .filter((s) => s.minBasePairLength != canonicalPrimer.basePairLength || s.primerLiterature != canonicalPrimer.reference)
      .map((s) => ({
        ...s,
        minBasePairLength: canonicalPrimer.basePairLength,
        maxBasePairLength: canonicalPrimer.maxBasePairLength,
        primerLiterature: canonicalPrimer.reference
      }))
    sp.samples = [...samplesCopy]
  })
  updatedSamplePools.forEach((sp) => {
    const negativeControlCopy = sp.negativeControls
      .filter((s) => s.minBasePairLength != canonicalPrimer.basePairLength || s.primerLiterature != canonicalPrimer.reference)
      .map((s) => ({
        ...s,
        minBasePairLength: canonicalPrimer.basePairLength,
        maxBasePairLength: canonicalPrimer.maxBasePairLength,
        primerLiterature: canonicalPrimer.reference
      }))
    sp.negativeControls = [...negativeControlCopy]
  })

  return [updatedSamplePools, canonicalPrimer]
}
